/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Isomorphic Dynamic Domain Resolution (WP 12.4 — Zero-Hardcoded-Domain)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 *
 * This module is the ONLY place in the monorepo allowed to know the default
 * production domain. Every other module must derive public origins from
 * `resolvePlatformDomains()` so that a white-label or partner deployment only
 * needs to change environment variables (BASE_DOMAIN, MEDIA_CDN_DOMAIN, ...).
 *
 * The module is runtime-agnostic (Workers, browser, Node CLI) and has no
 * dependency on `process.env` or `import.meta.env`; callers pass their own
 * environment record in.
 */

/** Fallback root domain used only when no environment override is supplied. */
export const DEFAULT_BASE_DOMAIN = 'sophiaplatform.com';

/** R2 key prefix reserved for shipped sample/default assets (see packages/seed-assets). */
export const SEED_ASSET_PREFIX = 'seed/';

/** Conventional subdomain labels; each can be overridden via SUBDOMAIN_* variables. */
export const DEFAULT_SUBDOMAINS = {
  app: 'app',
  admin: 'admin',
  api: 'api',
  media: 'media',
  docs: 'docs',
  assets: 'assets',
} as const;

export type PlatformSubdomain = keyof typeof DEFAULT_SUBDOMAINS;

/**
 * Raw configuration accepted by the resolver. Keys mirror Worker `[vars]` names;
 * frontends map their `VITE_*` variables onto the same shape.
 */
export interface DomainConfigInput {
  BASE_DOMAIN?: string;
  /** Absolute override for the public landing origin. */
  SITE_URL?: string;
  /** Absolute override for the user workspace origin (legacy name kept for Stripe redirects). */
  APP_URL?: string;
  /** Absolute override for the admin portal origin. */
  ADMIN_URL?: string;
  /** Absolute override for the edge API origin. */
  API_URL?: string;
  /** Absolute override for the R2 media CDN origin. */
  MEDIA_CDN_DOMAIN?: string;
  /** Absolute override for the developer docs origin. */
  DOCS_URL?: string;
  /** Absolute override for the static assets origin (widget.js). */
  ASSETS_CDN_DOMAIN?: string;
  SUBDOMAIN_APP?: string;
  SUBDOMAIN_ADMIN?: string;
  SUBDOMAIN_API?: string;
  SUBDOMAIN_MEDIA?: string;
  SUBDOMAIN_DOCS?: string;
  SUBDOMAIN_ASSETS?: string;
}

/** Fully resolved, normalized public origins (no trailing slash). */
export interface PlatformDomains {
  baseDomain: string;
  siteOrigin: string;
  appOrigin: string;
  adminOrigin: string;
  apiOrigin: string;
  mediaOrigin: string;
  docsOrigin: string;
  assetsOrigin: string;
}

/**
 * Strips protocol, path and trailing dots/slashes so that values such as
 * `https://MyBrand.ai/` and `mybrand.ai` resolve to the same bare host.
 */
export function normalizeDomain(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .replace(/\/.*$/, '')
    .replace(/\.+$/, '');
}

/** Ensures an origin has a scheme and no trailing slash. */
export function normalizeOrigin(value: string): string {
  const trimmed = value.trim().replace(/\/+$/, '');
  return /^[a-z]+:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

function pick(value: string | undefined): string | undefined {
  const v = value?.trim();
  return v ? v : undefined;
}

function subdomainOrigin(label: string | undefined, fallback: string, baseDomain: string): string {
  const resolvedLabel = (pick(label) ?? fallback).replace(/\.+$/, '');
  return `https://${resolvedLabel}.${baseDomain}`;
}

/**
 * Resolves every public origin of the platform from a single environment record.
 * Absolute overrides win over the `<subdomain>.<BASE_DOMAIN>` convention.
 */
export function resolvePlatformDomains(input: DomainConfigInput = {}): PlatformDomains {
  const baseDomain = normalizeDomain(pick(input.BASE_DOMAIN) ?? DEFAULT_BASE_DOMAIN);
  const override = (value: string | undefined): string | undefined => {
    const v = pick(value);
    return v ? normalizeOrigin(v) : undefined;
  };

  return {
    baseDomain,
    siteOrigin: override(input.SITE_URL) ?? `https://${baseDomain}`,
    appOrigin: override(input.APP_URL) ?? subdomainOrigin(input.SUBDOMAIN_APP, DEFAULT_SUBDOMAINS.app, baseDomain),
    adminOrigin:
      override(input.ADMIN_URL) ?? subdomainOrigin(input.SUBDOMAIN_ADMIN, DEFAULT_SUBDOMAINS.admin, baseDomain),
    apiOrigin: override(input.API_URL) ?? subdomainOrigin(input.SUBDOMAIN_API, DEFAULT_SUBDOMAINS.api, baseDomain),
    mediaOrigin:
      override(input.MEDIA_CDN_DOMAIN) ??
      subdomainOrigin(input.SUBDOMAIN_MEDIA, DEFAULT_SUBDOMAINS.media, baseDomain),
    docsOrigin:
      override(input.DOCS_URL) ?? subdomainOrigin(input.SUBDOMAIN_DOCS, DEFAULT_SUBDOMAINS.docs, baseDomain),
    assetsOrigin:
      override(input.ASSETS_CDN_DOMAIN) ??
      subdomainOrigin(input.SUBDOMAIN_ASSETS, DEFAULT_SUBDOMAINS.assets, baseDomain),
  };
}

/** Joins an origin and a relative path without producing double slashes. */
export function joinUrl(origin: string, path: string): string {
  const cleanOrigin = origin.replace(/\/+$/, '');
  const cleanPath = path.replace(/^\/+/, '');
  return cleanPath ? `${cleanOrigin}/${cleanPath}` : cleanOrigin;
}

/**
 * Turns a relative R2 storage key into a public CDN URL.
 * Absolute URLs (http/https/data/blob) are returned untouched so the function
 * is safe to call on values that were persisted before the relative-key migration.
 */
export function resolveMediaUrl(keyOrUrl: string | null | undefined, mediaOrigin: string): string {
  if (!keyOrUrl) return '';
  if (/^(https?:|data:|blob:)/i.test(keyOrUrl)) return keyOrUrl;
  return joinUrl(mediaOrigin, keyOrUrl);
}

/** Options for {@link isAllowedOrigin}. */
export interface OriginPolicyOptions {
  baseDomain: string;
  /** Allow http(s)://localhost and 127.0.0.1 on any port (development only). */
  allowLocalhost?: boolean;
  /** Allow Cloudflare Pages preview deployments (`*.pages.dev`). */
  allowPagesPreview?: boolean;
  /** Additional exact origins (e.g. a partner's custom marketing site). */
  extraOrigins?: readonly string[];
}

/**
 * Strict CORS origin matcher: the root domain and any single- or multi-level
 * subdomain of BASE_DOMAIN over HTTPS. Suffix tricks like `evil-sophiaplatform.com`
 * or `sophiaplatform.com.evil.io` are rejected because the host is compared
 * label-wise instead of with a naive `endsWith`.
 */
export function isAllowedOrigin(origin: string | null | undefined, options: OriginPolicyOptions): boolean {
  if (!origin) return false;

  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }

  const host = url.hostname.toLowerCase();

  if (options.allowLocalhost && (host === 'localhost' || host === '127.0.0.1')) {
    return url.protocol === 'http:' || url.protocol === 'https:';
  }

  if (url.protocol !== 'https:') return false;

  const base = normalizeDomain(options.baseDomain);
  if (host === base || host.endsWith(`.${base}`)) return true;

  if (options.allowPagesPreview && host.endsWith('.pages.dev')) return true;

  const normalizedOrigin = `${url.protocol}//${url.host}`;
  return (options.extraOrigins ?? []).some((o) => normalizeOrigin(o) === normalizedOrigin);
}
